import React from 'react';
import _ from 'underscore';

import FolderSelector from 'file-browser/FolderSelector';
import FileState from 'filestate/FileState';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

const ASSEMBLY_FOLDER_MODAL_KEY = 'AssemblyFolderModal';

interface BaseProps {
  createSequence: (sequenceFolderId: string, oligoFolderId: string) => JQueryPromise<FileState | undefined>;
}

interface Props extends BaseProps {
  onRequestClose: () => void;
}

interface State {
  sequenceFolderId: Optional<string>;
  oligoFolderId: Optional<string>;
  creating: boolean;
}

class AssemblyFolderModal extends React.Component<Props, State> {
  static open(props: BaseProps): void {
    LegacyModalContainer.open(
      ASSEMBLY_FOLDER_MODAL_KEY,
      <AssemblyFolderModal
        // eslint-disable-next-line no-restricted-syntax
        {..._.extend(props, {
          onRequestClose() {
            LegacyModalContainer.close(ASSEMBLY_FOLDER_MODAL_KEY);
          },
        })}
      />
    );
  }

  override state: State = {
    sequenceFolderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
    oligoFolderId: FolderDefaults.get(FolderDefaultsKey.OLIGO),
    creating: false,
  };

  _handleChangeSequenceFolder = (e: {target: {value: string}}): void => {
    this.setState({sequenceFolderId: e.target.value});
  };

  _handleChangeOligoFolder = (e: {target: {value: string}}): void => {
    this.setState({oligoFolderId: e.target.value});
  };

  handleSubmit = (): void => {
    this.setState({creating: true});
    this.props
      .createSequence(this.state.sequenceFolderId!, this.state.oligoFolderId!)
      .done(() => {
        FolderDefaults.set(FolderDefaultsKey.INVENTORY, this.state.sequenceFolderId!);
        FolderDefaults.set(FolderDefaultsKey.OLIGO, this.state.oligoFolderId!);
        this.props.onRequestClose();
      })
      .fail((res) => {
        notifications.notifyError(res);
        this.setState({creating: false});
      });
  };

  override render(): JSX.Element {
    const disabled =
      this.state.sequenceFolderId == null || this.state.oligoFolderId == null || this.state.creating;
    return (
      <Modal
        enableRedesign={false}
        title="Select Folders for Assembly"
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.handleSubmit}
        buttons={[
          {
            text: 'Create',
            submit: true,
            style: 'success',
            disabled,
            dataTestAttrs: {component: 'AssemblyFolderModal', element: 'createButton'},
          },
          {text: 'Cancel', cancel: true, disabled: this.state.creating},
        ]}
        dataTestAttrs={{component: 'AssemblyFolderModal', element: 'root'}}
      >
        <Row>
          <Col sm={6}>
            <FormGroup>
              <Label>Sequence Folder</Label>
              <FolderSelector
                showInventory={true}
                value={this.state.sequenceFolderId}
                onChange={this._handleChangeSequenceFolder}
                dataTestAttrs={{component: 'AssemblyFolderModal', element: 'sequenceFolderSelector'}}
                unsetOnFetchFail={true}
              />
            </FormGroup>
          </Col>
          <Col sm={6}>
            <FormGroup>
              <Label>Primer Folder</Label>
              <FolderSelector
                showInventory={true}
                value={this.state.oligoFolderId}
                onChange={this._handleChangeOligoFolder}
                dataTestAttrs={{component: 'AssemblyFolderModal', element: 'primerFolderSelector'}}
                unsetOnFetchFail={true}
              />
            </FormGroup>
          </Col>
        </Row>
      </Modal>
    );
  }
}

export default AssemblyFolderModal;
